import clsx from 'clsx';
import {
  ArrowLeft,
  ArrowUpDown,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
  Copy,
  Filter,
  Kanban,
  Layers,
  LayoutGrid,
  RefreshCw,
  Search,
  User,
} from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchJSON } from '../api';
import { apiPath } from '../constants';
import { useStore } from '../store';
import { showToast } from '../toast';
import type { EpisodeSummary } from '../types';

type ViewMode = 'grid' | 'board';
type SortMode = 'votes' | 'lease' | 'id' | 'status' | 'updated' | 'priority';
const STALE_LEASE_MS = 15 * 60 * 1000;

function computeEpisodeQueueHealth(
  episodes: EpisodeSummary[],
  options?: { nowMs?: number; staleLeaseMs?: number }
): { staleLeases: number; readyQueue: number } {
  const nowMs = options?.nowMs ?? Date.now();
  const staleLeaseMs = options?.staleLeaseMs ?? STALE_LEASE_MS;

  let staleLeases = 0;
  let readyQueue = 0;

  episodes.forEach((episode) => {
    const status = episode.mark_status ?? 'pending';
    if (status === 'pending' && !episode.lease_owner) {
      readyQueue += 1;
    }
    if (
      status !== 'completed' &&
      status !== 'audited' &&
      episode.lease_owner &&
      episode.lease_expires_at_ms &&
      nowMs - episode.lease_expires_at_ms >= staleLeaseMs
    ) {
      staleLeases += 1;
    }
  });

  return { staleLeases, readyQueue };
}

function timeAgo(ms: number | null | undefined): string {
  if (!ms) return '';
  const diff = Date.now() - ms;
  if (diff < 0) return '';
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}

export function EpisodeList() {
  const {
    episodes,
    total,
    page,
    pageSize,
    isLoading,
    summaryFilterStatus,
    isAdmin,
    statsCache,
    loadEpisodeSummaries,
    invalidateEpisodeSortCache,
    showEpisodeDebug,
    showEpisodeSpecify,
    showDashboard,
  } = useStore();

  const [searchId, setSearchId] = useState('');
  const [filterText, setFilterText] = useState('');
  const [sortMode, setSortMode] = useState<SortMode>('votes');
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [showStaleOnly, setShowStaleOnly] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    if (typeof window === 'undefined') return 'grid';
    const stored = window.localStorage.getItem('secshot_episode_view');
    return stored === 'board' ? 'board' : 'grid';
  });
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const interval = setInterval(() => {
      if (!isLoading) {
        void loadEpisodeSummaries({ forceReload: true, resetPage: false });
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [loadEpisodeSummaries, isLoading]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('secshot_episode_view', viewMode);
    }
  }, [viewMode]);

  useEffect(() => {
    const tick = setInterval(() => {
      setNow(Date.now());
    }, 60000);
    return () => clearInterval(tick);
  }, []);

  const handleRefresh = () => {
    void loadEpisodeSummaries({ forceReload: true, resetPage: false });
  };

  const handleBack = () => {
    void showDashboard();
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    useStore.setState({ summaryFilterStatus: val, page: 1 });
    invalidateEpisodeSortCache();
    void loadEpisodeSummaries({ forceReload: true, resetPage: true });
  };

  const handlePageChange = (newPage: number) => {
    useStore.setState({ page: newPage });
    void loadEpisodeSummaries({ resetPage: false });
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const size = parseInt(e.target.value, 10);
    if (size > 0) {
      useStore.setState({ pageSize: size, page: 1 });
      void loadEpisodeSummaries({ resetPage: false, forceReload: true });
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchId.trim()) {
      void showEpisodeDebug(searchId.trim());
    }
  };

  const handleToggleSelectionMode = () => {
    setSelectionMode((prev) => {
      if (prev) {
        setSelectedIds(new Set());
      }
      return !prev;
    });
  };

  const handleToggleSelected = (episodeId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(episodeId)) {
        next.delete(episodeId);
      } else {
        next.add(episodeId);
      }
      return next;
    });
  };

  const handleBulkCopy = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    await navigator.clipboard.writeText(ids.join('\n'));
    showToast(`Copied ${ids.length.toLocaleString()} episode IDs`);
  };

  const handleSelectFiltered = (episodeIds: string[]) => {
    setSelectedIds(new Set(episodeIds));
  };

  const handleBulkRequeue = async (target: 'pending' | 'completed') => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    try {
      setIsBulkUpdating(true);
      const response = await fetchJSON<{ moved: number; missing: string[] }>(
        apiPath('/admin/requeue'),
        {
          method: 'post',
          json: { episode_ids: ids, target_status: target },
        }
      );
      const moved = response?.moved ?? 0;
      const missing = response?.missing ?? [];
      showToast(
        `Requeued ${moved.toLocaleString()} episode${moved === 1 ? '' : 's'}${
          missing.length ? ` (${missing.length} missing)` : ''
        }`
      );
      setSelectedIds(new Set());
      setSelectionMode(false);
      invalidateEpisodeSortCache();
      await loadEpisodeSummaries({ forceReload: true, resetPage: false });
    } catch (err) {
      showToast(`Bulk requeue failed: ${(err as Error).message}`);
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const formatLeaseRemaining = (expiresAtMs?: number | null) => {
    if (!expiresAtMs) return null;
    const diff = expiresAtMs - now;
    if (diff <= 0) return 'Expired';
    const totalMinutes = Math.ceil(diff / 60000);
    if (totalMinutes < 60) return `${totalMinutes}m`;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  const formatDeviceLabel = (deviceTypeEnum?: number | null) => {
    if (deviceTypeEnum === 1) return 'Device 1';
    if (deviceTypeEnum === 2) return 'Device 2';
    if (deviceTypeEnum == null) return 'Default';
    return `Device ${deviceTypeEnum}`;
  };

  const isStaleLeaseEpisode = useCallback(
    (episode: EpisodeSummary) => {
      if (!episode.lease_owner || !episode.lease_expires_at_ms) return false;
      const status = episode.mark_status ?? 'pending';
      if (status === 'completed' || status === 'audited') return false;
      return now - episode.lease_expires_at_ms >= STALE_LEASE_MS;
    },
    [now]
  );

  const filteredEpisodes = useMemo(() => {
    let filtered = [...episodes];

    // Status filtering is now server-side via summaryFilterStatus

    const query = filterText.trim().toLowerCase();
    if (query) {
      filtered = filtered.filter((episode) => {
        if (episode.id.toLowerCase().includes(query)) return true;
        if (episode.lease_owner?.toLowerCase().includes(query)) return true;
        return episode.operators?.some((op) => op.toLowerCase().includes(query)) ?? false;
      });
    }

    if (showStaleOnly) {
      filtered = filtered.filter((episode) => isStaleLeaseEpisode(episode));
    }

    return filtered;
  }, [episodes, filterText, showStaleOnly, isStaleLeaseEpisode]);

  const sortedEpisodes = useMemo(() => {
    const sorted = [...filteredEpisodes];

    if (sortMode === 'id') {
      sorted.sort((a, b) => a.id.localeCompare(b.id));
      return sorted;
    }

    if (sortMode === 'updated') {
      sorted.sort((a, b) => {
        const aTime = a.last_updated_ms ?? 0;
        const bTime = b.last_updated_ms ?? 0;
        return bTime - aTime;
      });
      return sorted;
    }

    if (sortMode === 'lease') {
      sorted.sort((a, b) => {
        const aLease = a.lease_expires_at_ms ?? Number.POSITIVE_INFINITY;
        const bLease = b.lease_expires_at_ms ?? Number.POSITIVE_INFINITY;
        if (aLease !== bLease) return aLease - bLease;
        return b.operator_count - a.operator_count || a.id.localeCompare(b.id);
      });
      return sorted;
    }

    if (sortMode === 'status') {
      const rank = (episode: EpisodeSummary) => {
        if (episode.mark_status === 'audited') return 3;
        if (episode.mark_status === 'completed') return 2;
        if (episode.mark_status === 'voting') return 1;
        return 0;
      };
      sorted.sort((a, b) => {
        const diff = rank(a) - rank(b);
        if (diff !== 0) return diff;
        return a.id.localeCompare(b.id);
      });
      return sorted;
    }

    if (sortMode === 'priority') {
      sorted.sort((a, b) => {
        const diff = (b.priority ?? 9) - (a.priority ?? 9);
        if (diff !== 0) return diff;
        return a.id.localeCompare(b.id);
      });
      return sorted;
    }

    sorted.sort((a, b) => {
      if (a.operator_count !== b.operator_count) {
        return b.operator_count - a.operator_count;
      }
      const aCompleted = a.mark_status === 'completed' || a.mark_status === 'audited';
      const bCompleted = b.mark_status === 'completed' || b.mark_status === 'audited';
      if (aCompleted !== bCompleted) return aCompleted ? 1 : -1;
      const aAnnot = a.operators && a.operators.length > 0 ? 0 : 1;
      const bAnnot = b.operators && b.operators.length > 0 ? 0 : 1;
      if (aAnnot !== bAnnot) return aAnnot - bAnnot;
      return a.id.localeCompare(b.id);
    });
    return sorted;
  }, [filteredEpisodes, sortMode]);

  const totalPages = total === 0 ? 1 : Math.max(1, Math.ceil(total / (pageSize || 48)));
  const currentPage = Math.min(Math.max(page, 1), totalPages);
  const startIndex = total === 0 ? 0 : (currentPage - 1) * (pageSize || 48);
  const endIndex = total === 0 ? 0 : Math.min(startIndex + (pageSize || 48), total);
  const sizeOptions = [500, 1000, 2000];
  const hasFilters = summaryFilterStatus !== 'all' || filterText.trim().length > 0;

  const queueCount = filteredEpisodes.filter(
    (episode) => episode.mark_status !== 'completed' && !episode.lease_owner
  ).length;
  const activeCount = filteredEpisodes.filter(
    (episode) => episode.mark_status !== 'completed' && !!episode.lease_owner
  ).length;
  const completedCount = filteredEpisodes.filter(
    (episode) => episode.mark_status === 'completed'
  ).length;
  const staleLeaseCount = filteredEpisodes.filter((episode) => isStaleLeaseEpisode(episode)).length;
  const queueHealth = useMemo(
    () => computeEpisodeQueueHealth(episodes, { nowMs: now, staleLeaseMs: STALE_LEASE_MS }),
    [episodes, now]
  );
  const showEmptyState = !isLoading && sortedEpisodes.length === 0;
  const emptyTitle = hasFilters ? 'No matches found' : 'All caught up!';
  const emptySubtitle = hasFilters
    ? 'Try adjusting filters or clearing the search.'
    : 'No episodes found matching current filters.';

  const renderEpisodeCard = (ep: EpisodeSummary) => {
    const isAudited = ep.mark_status === 'audited';
    const isCompleted = ep.mark_status === 'completed';
    const inProgress = ep.mark_status === 'voting';
    const voteCount = Math.max(0, ep.operator_count || 0);
    const progressTarget = statsCache?.required_votes ?? 2;
    const progressRatio = isAudited || isCompleted ? 1 : Math.min(1, voteCount / progressTarget);
    const progressPercent = Math.round(progressRatio * 100);
    const leaseRemaining = formatLeaseRemaining(ep.lease_expires_at_ms);
    const deviceLabel = formatDeviceLabel(ep.device_type_enum);
    const statusLabel = isAudited
      ? 'Audited'
      : isCompleted
        ? 'Completed'
        : inProgress
          ? 'Voting'
          : 'Pending';
    const statusTone = isAudited
      ? 'border-blue-500/20 text-blue-300 bg-blue-500/10'
      : isCompleted
        ? 'border-emerald-500/20 text-emerald-300 bg-emerald-500/10'
        : inProgress
          ? 'border-purple-500/20 text-purple-300 bg-purple-500/10'
          : 'border-brand-orange/20 text-brand-orange bg-brand-orange/10';

    let lastOperator = null;
    if (ep.operators && ep.operators.length > 0) {
      lastOperator = ep.operators[ep.operators.length - 1];
    }

    return (
      <div
        key={ep.id}
        className={clsx(
          'group relative flex flex-col justify-between gap-4 rounded-xl border bg-brand-surface p-5 transition-all hover:-translate-y-1 hover:shadow-lg',
          isCompleted
            ? 'border-brand-border hover:border-emerald-500/50'
            : inProgress
              ? 'border-brand-border hover:border-amber-500/50'
              : 'border-brand-border hover:border-brand-orange/50'
        )}
      >
        <div>
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <div className="text-sm font-mono font-bold text-brand-text-primary break-all">
                {ep.id}
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  void navigator.clipboard.writeText(ep.id);
                  showToast('Episode ID copied');
                }}
                className="p-1 text-brand-text-secondary hover:text-white transition-colors opacity-0 group-hover:opacity-100"
                title="Copy ID"
              >
                <Copy className="h-3 w-3" />
              </button>
            </div>
            <span
              className={clsx(
                'text-[10px] uppercase tracking-wide border rounded-full px-2 py-0.5 whitespace-nowrap',
                statusTone
              )}
            >
              {statusLabel}
            </span>
          </div>
          {isAdmin && selectionMode && (
            <div className="mt-2 flex items-center gap-2 text-xs text-brand-text-secondary">
              <input
                type="checkbox"
                checked={selectedIds.has(ep.id)}
                onChange={() => handleToggleSelected(ep.id)}
                className="h-3.5 w-3.5 rounded border-brand-border bg-brand-dark text-brand-orange focus:ring-brand-orange"
              />
              <span>Select</span>
            </div>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-wider text-brand-text-secondary">
            <span className="rounded-full border border-brand-border bg-brand-dark px-2 py-0.5">
              {deviceLabel}
            </span>
            <span className="rounded-full border border-brand-border bg-brand-dark px-2 py-0.5">
              Votes {voteCount.toLocaleString()}
            </span>
            <span className="rounded-full border border-brand-border bg-brand-dark px-2 py-0.5">
              Priority {ep.priority ?? 9}
            </span>
            {ep.last_updated_ms && (
              <span className="rounded-full border border-brand-border bg-brand-dark px-2 py-0.5 flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {timeAgo(ep.last_updated_ms)}
              </span>
            )}
            {!isCompleted && ep.lease_owner && (
              <span className="rounded-full border border-brand-border bg-brand-dark px-2 py-0.5 flex items-center gap-1 text-brand-orange/80 border-brand-orange/20">
                <Clock className="h-3 w-3" />
                {ep.lease_owner} {leaseRemaining ? `(${leaseRemaining})` : ''}
              </span>
            )}
            {!isCompleted && !ep.lease_owner && (
              <span className="rounded-full border border-brand-border bg-brand-dark px-2 py-0.5 opacity-50">
                Available
              </span>
            )}
            {lastOperator && (
              <span className="rounded-full border border-brand-border bg-brand-dark px-2 py-0.5 flex items-center gap-1">
                <User className="h-3 w-3" />
                {isCompleted ? `By ${lastOperator}` : `Last: ${lastOperator}`}
              </span>
            )}
          </div>
          <div className="mt-3">
            <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-brand-text-secondary">
              <span>Progress</span>
              <span>
                {voteCount}/{progressTarget} · {progressPercent}%
              </span>
            </div>
            <div className="mt-1 h-1.5 w-full rounded-full bg-brand-dark">
              <div
                className={clsx(
                  'h-1.5 rounded-full transition-all',
                  isAudited
                    ? 'bg-blue-400'
                    : isCompleted
                      ? 'bg-emerald-400'
                      : inProgress
                        ? 'bg-purple-400'
                        : 'bg-brand-orange'
                )}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
        </div>

        <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            type="button"
            onClick={() =>
              void showEpisodeSpecify(ep.id, {
                intent: isAdmin ? 'admin-readonly' : 'operator-claim',
                autoSubmit: true,
              })
            }
            className="flex-1 rounded-lg bg-brand-orange px-3 py-1.5 text-xs font-semibold text-white shadow hover:bg-brand-orange-hover"
          >
            {isAdmin ? 'View' : 'Annotate'}
          </button>
          <button
            type="button"
            onClick={() => void showEpisodeDebug(ep.id)}
            className="rounded-lg border border-brand-border bg-brand-dark px-3 py-1.5 text-xs font-semibold text-brand-text-secondary hover:text-white"
          >
            Debug
          </button>
        </div>
      </div>
    );
  };

  const renderBoardColumn = (title: string, items: EpisodeSummary[], colorClass: string) => (
    <div className="flex flex-col gap-4 min-w-[300px] flex-1">
      <div className={clsx('flex items-center gap-2 pb-2 border-b-2', colorClass)}>
        <span className="font-semibold text-brand-text-primary">{title}</span>
        <span className="text-xs text-brand-text-secondary bg-brand-dark px-2 py-0.5 rounded-full">
          {items.length}
        </span>
      </div>
      <div className="flex flex-col gap-3">
        {items.map(renderEpisodeCard)}
        {items.length === 0 && (
          <div className="p-8 text-center border border-dashed border-brand-border rounded-xl text-brand-text-secondary text-sm bg-brand-surface/30">
            Empty
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 border-b border-brand-border pb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-white flex items-center gap-3">
            <Layers className="h-8 w-8 text-brand-orange" />
            Episodes
          </h1>
          <p className="mt-2 text-brand-text-secondary">Manage and monitor annotation flow.</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <form onSubmit={handleSearch} className="relative group">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-brand-text-secondary group-focus-within:text-brand-orange" />
            <input
              type="text"
              placeholder="Find ID..."
              value={searchId}
              onChange={(e) => setSearchId(e.target.value)}
              className="h-9 w-40 rounded-lg border border-brand-border bg-brand-surface pl-8 pr-3 text-xs text-brand-text-primary focus:border-brand-orange focus:outline-none focus:ring-1 focus:ring-brand-orange transition-all"
            />
          </form>

          <div className="h-6 w-px bg-brand-border mx-1"></div>

          {isAdmin && (
            <button
              type="button"
              onClick={handleToggleSelectionMode}
              className={clsx(
                'rounded-lg border px-3 py-2 text-xs font-medium transition',
                selectionMode
                  ? 'border-brand-orange text-brand-orange'
                  : 'border-brand-border bg-brand-surface text-brand-text-secondary hover:text-white hover:border-brand-text-secondary/50'
              )}
            >
              {selectionMode ? 'Exit Selection' : 'Select'}
            </button>
          )}

          <button
            type="button"
            onClick={handleRefresh}
            className="flex items-center gap-2 rounded-lg border border-brand-border bg-brand-surface px-3 py-2 text-xs font-medium text-brand-text-secondary transition hover:text-white hover:border-brand-text-secondary/50"
            title="Refresh list"
          >
            <RefreshCw className={clsx('h-3.5 w-3.5', isLoading && 'animate-spin')} />
            Refresh
          </button>

          <button
            type="button"
            onClick={handleBack}
            className="flex items-center gap-2 rounded-lg border border-brand-border bg-transparent px-3 py-2 text-xs font-medium text-brand-text-secondary transition hover:text-white hover:border-brand-text-secondary/50"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </button>
        </div>
      </div>

      {isAdmin && selectionMode && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-brand-border bg-brand-surface p-4 text-xs text-brand-text-secondary">
          <span>
            Selected {selectedIds.size.toLocaleString()}
            {selectedIds.size === 1 ? ' episode' : ' episodes'}
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => handleSelectFiltered(sortedEpisodes.map((episode) => episode.id))}
              disabled={sortedEpisodes.length === 0 || isBulkUpdating}
              className="rounded-md border border-brand-border px-2.5 py-1 text-xs font-semibold text-brand-text-secondary hover:text-white disabled:opacity-50"
            >
              Select Filtered
            </button>
            <button
              type="button"
              onClick={() => void handleBulkCopy()}
              disabled={selectedIds.size === 0 || isBulkUpdating}
              className="rounded-md border border-brand-border px-2.5 py-1 text-xs font-semibold text-brand-text-secondary hover:text-white disabled:opacity-50"
            >
              Copy IDs
            </button>
            <button
              type="button"
              onClick={() => void handleBulkRequeue('pending')}
              disabled={selectedIds.size === 0 || isBulkUpdating}
              className="rounded-md border border-brand-border px-2.5 py-1 text-xs font-semibold text-brand-text-secondary hover:text-white disabled:opacity-50"
            >
              Requeue Pending
            </button>
            <button
              type="button"
              onClick={() => void handleBulkRequeue('completed')}
              disabled={selectedIds.size === 0 || isBulkUpdating}
              className="rounded-md border border-brand-border px-2.5 py-1 text-xs font-semibold text-brand-text-secondary hover:text-white disabled:opacity-50"
            >
              Requeue Completed
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              disabled={selectedIds.size === 0 || isBulkUpdating}
              className="rounded-md border border-brand-border px-2.5 py-1 text-xs font-semibold text-brand-text-secondary hover:text-white disabled:opacity-50"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl border border-brand-border bg-brand-surface p-5">
          <div className="text-xs uppercase font-semibold tracking-wider text-brand-text-secondary">
            Queue
          </div>
          <div className="mt-2 text-2xl font-bold text-brand-text-primary font-mono">
            {queueCount.toLocaleString()}
          </div>
        </div>
        <div className="rounded-xl border border-brand-border bg-brand-surface p-5">
          <div className="text-xs uppercase font-semibold tracking-wider text-brand-text-secondary">
            Active
          </div>
          <div className="mt-2 text-2xl font-bold text-brand-text-primary font-mono">
            {activeCount.toLocaleString()}
          </div>
        </div>
        <div className="rounded-xl border border-brand-border bg-brand-surface p-5">
          <div className="text-xs uppercase font-semibold tracking-wider text-brand-text-secondary">
            Completed
          </div>
          <div className="mt-2 text-2xl font-bold text-brand-text-primary font-mono">
            {completedCount.toLocaleString()}
          </div>
        </div>
        <div className="rounded-xl border border-brand-border bg-brand-surface p-5">
          <div className="text-xs uppercase font-semibold tracking-wider text-brand-text-secondary">
            Stale Leases
          </div>
          <div className="mt-2 text-2xl font-bold text-brand-text-primary font-mono">
            {queueHealth.staleLeases.toLocaleString()}
          </div>
          <div className="text-xs text-brand-text-secondary">
            {queueHealth.readyQueue.toLocaleString()} ready and unleased
          </div>
        </div>
      </div>

      {/* Controls Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-brand-border bg-brand-surface p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="h-3.5 w-3.5 text-brand-text-secondary" />
            <select
              value={summaryFilterStatus}
              onChange={handleStatusChange}
              className="rounded-lg border border-brand-border bg-brand-dark px-2 py-1 text-xs text-brand-text-primary focus:border-brand-orange focus:outline-none"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="voting">Voting</option>
              <option value="completed">Completed</option>
              <option value="audited">Audited</option>
            </select>
          </div>

          <div className="h-4 w-px bg-brand-border hidden sm:block"></div>

          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-brand-text-secondary" />
            <input
              type="text"
              placeholder="Filter list..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="h-8 w-44 rounded-lg border border-brand-border bg-brand-dark pl-8 pr-3 text-xs text-brand-text-primary focus:border-brand-orange focus:outline-none focus:ring-1 focus:ring-brand-orange"
            />
          </div>

          <div className="h-4 w-px bg-brand-border hidden sm:block"></div>

          <button
            type="button"
            onClick={() => setShowStaleOnly((prev) => !prev)}
            className={clsx(
              'rounded-lg border px-2.5 py-1 text-xs font-semibold transition',
              showStaleOnly
                ? 'border-brand-orange text-brand-orange'
                : 'border-brand-border text-brand-text-secondary hover:text-white'
            )}
            title="Show episodes with stale leases only"
          >
            Stale Only {staleLeaseCount > 0 ? `(${staleLeaseCount})` : ''}
          </button>

          <div className="h-4 w-px bg-brand-border hidden sm:block"></div>

          <div className="flex items-center gap-2">
            <ArrowUpDown className="h-3.5 w-3.5 text-brand-text-secondary" />
            <select
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value as SortMode)}
              className="rounded-lg border border-brand-border bg-brand-dark px-2 py-1 text-xs text-brand-text-primary focus:border-brand-orange focus:outline-none"
            >
              <option value="votes">Most Votes</option>
              <option value="priority">Priority</option>
              <option value="lease">Lease Expiry</option>
              <option value="status">Status</option>
              <option value="id">ID</option>
              <option value="updated">Last Updated</option>
            </select>
          </div>

          <div className="h-4 w-px bg-brand-border hidden sm:block"></div>

          {/* View Mode Toggle */}
          <div className="flex items-center bg-brand-dark rounded-lg p-0.5 border border-brand-border">
            <button
              type="button"
              onClick={() => setViewMode('grid')}
              className={clsx(
                'p-1.5 rounded-md transition-all',
                viewMode === 'grid'
                  ? 'bg-brand-surface text-white shadow-sm'
                  : 'text-brand-text-secondary hover:text-white'
              )}
              title="Grid View"
            >
              <LayoutGrid className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => setViewMode('board')}
              className={clsx(
                'p-1.5 rounded-md transition-all',
                viewMode === 'board'
                  ? 'bg-brand-surface text-white shadow-sm'
                  : 'text-brand-text-secondary hover:text-white'
              )}
              title="Board View"
            >
              <Kanban className="h-3.5 w-3.5" />
            </button>
          </div>

          <div className="h-4 w-px bg-brand-border hidden sm:block"></div>

          <div className="flex items-center gap-2">
            <span className="text-xs text-brand-text-secondary uppercase font-bold tracking-wider">
              Per Page
            </span>
            <select
              value={pageSize}
              onChange={handlePageSizeChange}
              className="rounded-lg border border-brand-border bg-brand-dark px-2 py-1 text-xs text-brand-text-primary focus:border-brand-orange focus:outline-none"
            >
              {sizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Pagination */}
        <div className="flex items-center gap-3 text-sm text-brand-text-secondary">
          <span className="hidden sm:inline">
            {(startIndex + 1).toLocaleString()}-{endIndex.toLocaleString()} of{' '}
            {total.toLocaleString()}
            {hasFilters && (
              <span className="ml-2 text-brand-orange">
                Filtered {sortedEpisodes.length.toLocaleString()}
              </span>
            )}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1}
              className="p-1 rounded hover:bg-brand-surface-highlight disabled:opacity-30"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="min-w-[3rem] text-center font-mono">
              {currentPage} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="p-1 rounded hover:bg-brand-surface-highlight disabled:opacity-30"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      {showEmptyState ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-brand-border bg-brand-surface/50 p-12 text-center">
          <CheckCircle className="h-10 w-10 text-brand-text-secondary mb-4" />
          <h3 className="text-lg font-medium text-brand-text-primary">{emptyTitle}</h3>
          <p className="text-brand-text-secondary">{emptySubtitle}</p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {sortedEpisodes.map(renderEpisodeCard)}
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row gap-6 overflow-x-auto pb-4">
          {renderBoardColumn(
            'Queue',
            sortedEpisodes.filter((e) => e.mark_status === 'pending' && !e.lease_owner),
            'border-brand-text-secondary/50'
          )}
          {renderBoardColumn(
            'Active',
            sortedEpisodes.filter(
              (e) => e.mark_status === 'voting' || (e.mark_status === 'pending' && !!e.lease_owner)
            ),
            'border-brand-orange'
          )}
          {renderBoardColumn(
            'Completed',
            sortedEpisodes.filter((e) => e.mark_status === 'completed'),
            'border-emerald-500'
          )}
          {renderBoardColumn(
            'Audited',
            sortedEpisodes.filter((e) => e.mark_status === 'audited'),
            'border-blue-500'
          )}
        </div>
      )}
    </div>
  );
}
